import { forwardRef } from 'react';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import Picto from '../../atoms/Picto/Picto';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

// Style
import style from './ModalTrigger.module.css';

const ModalTrigger = forwardRef(
  /**
   * ModalTrigger component - A trigger button with icon and optional children (e.g., notifications)
   * @param {object} props
   * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} props.onClick - Click handler function
   * @param {keyof typeof import('../../atoms/Picto/Picto').availablePictos} props.icon - Icon name from available pictos
   * @param {'left'|'right'} [props.iconPosition='left'] - Icon position relative to children
   * @param {React.ReactNode} [props.children] - Children elements (e.g., notification component)
   * @param {string} [props.className] - Additional CSS classes
   * @param {boolean} [props.disabled=false] - Whether button is disabled
   * @param {string} [props.title] - Tooltip text for accessibility
   * @param {string} [props.label] - Label text for accessibility
   * @param {string} [props.maxWidth='100%'] - Maximum width of the button
   * @param {ApiPageTemplate} [props.template] - Template of the button
   * @param {boolean} [props.isLoading=false] - Whether button is in loading state
   * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref for the button element
   * @returns {JSX.Element} ModalTrigger component
   */
  ({
    onClick,
    icon,
    iconPosition = 'left',
    children,
    label = '',
    className,
    disabled = false,
    title,
    maxWidth = '100%',
    template = 'table',
    isLoading = false,
  }, ref) => {
    if (isLoading) {
      return (
        <Skeleton
          shape="rectangle"
          width={maxWidth}
          height="100%"
          className={cn([
            style['modal-trigger'],
            style[iconPosition],
            style[template],
            disabled && style.disabled,
            className,
          ])}
        />
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        className={cn([
          style['modal-trigger'],
          style[iconPosition],
          style[template],
          disabled && style.disabled,
          className,
        ])}
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-label={title}
        style={{ maxWidth }}
      >
        {iconPosition === 'left' ? (
          <div className={style.icon}>
            <Picto icon={icon} />
          </div>
        ) : null}

        {label && <div className={style.label}>{label}</div>}

        {iconPosition === 'right' ? (
          <div className={style.icon}>
            <Picto icon={icon} />
          </div>
        ) : null}

        {children && <div className={style.content}>{children}</div>}
      </button>
    );
  },
);

export default ModalTrigger;
